import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
} 

export const validateEmail = (value: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Sanitize a URL: trim whitespace, remove trailing dots, lowercase domain,
 * collapse double slashes in path.
 */
export function normalizeUrl(url: string): string {
  let cleaned = url.trim();

  // Remove trailing dots
  if (/^https?:\/\//.test(cleaned)) {
    const protocolEnd = cleaned.indexOf("://") + 3;
    cleaned = cleaned.slice(0, protocolEnd) + cleaned.slice(protocolEnd).replace(/\.+$/, "");
  } else {
    cleaned = cleaned.replace(/\.+$/, "");
  }

  // Strip path/query/fragment — keep only the domain
  if (/^https?:\/\//.test(cleaned)) {
    const protocolEnd = cleaned.indexOf("://") + 3;
    const rest = cleaned.slice(protocolEnd);
    const slashIdx = rest.indexOf("/");
    const domain = slashIdx === -1 ? rest : rest.slice(0, slashIdx);
    cleaned = cleaned.slice(0, protocolEnd) + domain.toLowerCase();
  } else {
    // No protocol — take only the domain (before first /)
    cleaned = cleaned.split("/")[0].toLowerCase();
  }

  // Always ensure https:// prefix
  if (!/^https?:\/\//.test(cleaned)) {
    cleaned = "https://" + cleaned;
  }

  return cleaned;
}

/**
 * Validate a URL string. Returns an error message if invalid, or null if valid.
 */
export function validateUrl(url: string): string | null {
  const normalized = normalizeUrl(url);

  if (!normalized) {
    return "URL cannot be empty.";
  }

  // Add protocol for parsing
  const withProtocol = /^https?:\/\//.test(normalized)
    ? normalized
    : `https://${normalized}`;

  try {
    const parsed = new URL(withProtocol);
    const domain = parsed.hostname;

    if (!/^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(domain)) {
      return "Please enter a valid domain (e.g., www.example.com).";
    }

    return null;
  } catch {
    return "Please enter a valid URL (e.g., www.example.com).";
  }
}